import type { Card } from '@braze/web-sdk';
import { DEFAULT_NOTIFICATION_TEMPLATE_ID } from '../constants/notificationTemplates';

interface CardGroups {
    pinned: Element[];
    criticalActions: Element[];
    systemNotices: Element[];
    growthTips: Element[];
}

const SECTION_IDS = {
    criticalActions: 'section-template-3',
    growthTips: 'section-template-2',
    systemNotices: 'section-template-1',
} as const;
interface ExtendedCard extends Card {
    title?: string;
}
const BRAZE_SELECTORS = {
    feedBody: '.ab-feed-body',
    card: '.ab-card',
} as const;

/**
 * Groups cards by template and inserts section headers in the feed
 */
export function groupCardsAndInsertHeaders(cards: ExtendedCard[]): void {
    const feedBody = findFeedBody();
    if (!feedBody || feedBody.dataset.ebGrouped === 'true') return;

    const existingCards = getExistingCardElements(feedBody);
    if (existingCards.length === 0) return;

    const { pinned, criticalActions, systemNotices, growthTips } =
        classifyCardsIntoGroups(cards, existingCards);

    appendGroupedSectionsToFeedBody(feedBody, {
        pinned,
        criticalActions,
        systemNotices,
        growthTips,
    });
}

function findFeedBody(): HTMLElement | null {
    return document.querySelector(
        BRAZE_SELECTORS.feedBody,
    ) as HTMLElement | null;
}

function getExistingCardElements(feedBody: HTMLElement): HTMLElement[] {
    return Array.from(
        feedBody.querySelectorAll(BRAZE_SELECTORS.card),
    ) as HTMLElement[];
}

function classifyCardsIntoGroups(
    cards: ExtendedCard[],
    existingCards: HTMLElement[],
): CardGroups {
    const pinned: Element[] = [];
    const criticalActions: Element[] = [];
    const systemNotices: Element[] = [];
    const growthTips: Element[] = [];

    cards.forEach((card, idx) => {
        const el = existingCards[idx] as HTMLElement;
        if (!el) return;

        const templateNumber =
            Number(card?.extras?.template) || DEFAULT_NOTIFICATION_TEMPLATE_ID;

        el.dataset.ebCardId = card.id || '';
        el.dataset.ebTemplate = String(templateNumber);
        el.dataset.ebPinned = String(card.pinned || false);

        // Pinned cards go on top, regardless of template
        if (card.pinned) {
            pinned.push(el);
            return;
        }

        if (card.isControl !== true) {
            // ignore control cards, we don't want to show them in the UI (title is empty)

            if (templateNumber === 3) {
                // Non-pinned cards are grouped by template
                criticalActions.push(el);
            } else if (templateNumber === 2) {
                growthTips.push(el);
            } else if (templateNumber === 1) {
                systemNotices.push(el);
            }
        }
    });
    return { pinned, criticalActions, growthTips, systemNotices };
}

function appendGroupedSectionsToFeedBody(
    feedBody: HTMLElement,
    groups: CardGroups,
): void {
    const fragment = document.createDocumentFragment();

    // Add pinned cards at the top without any section header
    if (groups.pinned.length > 0) {
        groups.pinned.forEach((el) => fragment.appendChild(el));
    }

    if (groups.criticalActions.length > 0) {
        const section = createSectionContainer(
            'Critical Actions',
            'Important info about your payouts or account that need your immediate attention.',
            SECTION_IDS.criticalActions,
        );
        groups.criticalActions.forEach((el) => section.appendChild(el));
        fragment.appendChild(section);
    }

    if (groups.systemNotices.length > 0) {
        const section = createSectionContainer(
            'System Notices',
            'Updates about site activity or incidents that may affect your events.',
            SECTION_IDS.systemNotices,
        );
        groups.systemNotices.forEach((el) => section.appendChild(el));
        fragment.appendChild(section);
    }

    if (groups.growthTips.length > 0) {
        const section = createSectionContainer(
            'Growth Tips',
            'Personalized suggestions to help you make the most of your events and tools.',
            SECTION_IDS.growthTips,
        );
        groups.growthTips.forEach((el) => section.appendChild(el));
        fragment.appendChild(section);
    }

    feedBody.innerHTML = '';
    feedBody.appendChild(fragment);
    feedBody.dataset.ebGrouped = 'true';
}

function createSectionContainer(
    title: string,
    subtitle: string,
    sectionId: string,
): HTMLElement {
    const headerContent = document.createElement('div');
    headerContent.className = 'sectionCardsContent';
    headerContent.id = sectionId;

    const titleDiv = document.createElement('div');
    titleDiv.className = 'sectionTitle';
    titleDiv.textContent = title;

    const subtitleDiv = document.createElement('div');
    subtitleDiv.className = 'sectionSubtitle';
    subtitleDiv.textContent = subtitle;

    headerContent.appendChild(titleDiv);
    headerContent.appendChild(subtitleDiv);

    return headerContent;
}

/**
 * Gets the section ID based on template number
 */
export function getSectionIdByTemplate(templateNumber: string): string | null {
    const template = Number(templateNumber);
    switch (template) {
        case 3:
            return SECTION_IDS.criticalActions;
        case 2:
            return SECTION_IDS.growthTips;
        case 1:
            return SECTION_IDS.systemNotices;
        default:
            return null;
    }
}

/**
 * Removes a section header if all cards within that section have been closed
 * Should be called AFTER the card element has been removed from the DOM
 */
export function removeSectionIfEmpty(sectionId: string): void {
    const sectionContainer = document.getElementById(sectionId);
    if (!sectionContainer) return;

    const remainingCards = sectionContainer.querySelectorAll(
        BRAZE_SELECTORS.card,
    );
    if (remainingCards.length === 0) {
        sectionContainer.remove();
    }
}
